<script setup lang="ts">
import { NCard, NSpace, NButton } from 'naive-ui';
import { ref, onUnmounted } from 'vue';
import type { WidgetConfig } from '@awesomeclocks/types';

interface Props {
  widget: WidgetConfig;
  isEditing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isEditing: false,
});

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  configure: [];
}>();

const isEditingName = ref(false);
const localName = ref(props.widget.name);
const wrapperRef = ref<HTMLElement | null>(null);

const startEditName = () => {
  if (isEditingName.value) {
    return;
  }
  localName.value = props.widget.name;
  isEditingName.value = true;
};

const saveName = () => {
  if (localName.value.trim()) {
    emit('updateName', localName.value.trim());
  }
  isEditingName.value = false;
};

const cancelEditName = () => {
  localName.value = props.widget.name;
  isEditingName.value = false;
};

const isFullscreen = () => {
  return document.fullscreenElement === wrapperRef.value;
};

const toggleFullscreen = async () => {
  const element = wrapperRef.value;
  if (!element) {
    return;
  }

  if (document.fullscreenElement) {
    await document.exitFullscreen();
    return;
  }

  if (element.requestFullscreen) {
    await element.requestFullscreen();
  }
};

onUnmounted(() => {
  if (isFullscreen()) {
    document.exitFullscreen();
  }
});
</script>

<template>
  <div ref="wrapperRef" class="widget-frame">
    <NCard
      :title="widget.name"
      :bordered="true"
      :segmented="{
        content: true,
        footer: 'soft',
      }"
      class="widget-wrapper"
    >
      <template #header>
        <NSpace justify="space-between" align="center" class="no-drag">
          <div
            v-if="!isEditingName"
            class="widget-title no-drag"
            @mousedown.stop
            @click.stop="startEditName"
          >
            {{ widget.name }}
          </div>
          <input
            v-else
            v-model="localName"
            class="widget-title-input no-drag"
            autofocus
            @mousedown.stop
            @click.stop
            @blur="saveName"
            @keyup.enter="saveName"
            @keyup.esc="cancelEditName"
          />
        </NSpace>
      </template>

      <template #header-extra>
        <NSpace class="no-drag">
          <NButton size="tiny" quaternary @click.stop="toggleFullscreen"> ⛶ </NButton>
          <NButton size="tiny" quaternary @click.stop="startEditName"> ✎ </NButton>
          <NButton size="tiny" quaternary @click.stop="emit('configure')"> ⚙️ </NButton>
          <NButton size="tiny" quaternary @click.stop="emit('remove')"> ✕ </NButton>
        </NSpace>
      </template>

      <div class="widget-content">
        <slot />
      </div>
    </NCard>
  </div>
</template>

<style scoped>
.widget-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.widget-wrapper :deep(.n-card__content) {
  flex: 1;
  display: flex;
  min-height: 0;
}

.widget-wrapper :deep(.n-card__content) > .widget-content {
  flex: 1;
}

.widget-title {
  cursor: pointer;
  user-select: none;
}

.widget-title:hover {
  opacity: 0.8;
}

.widget-title-input {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: inherit;
  font: inherit;
  padding: 2px 6px;
  min-width: 120px;
}

.widget-title-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.45);
}

.widget-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
.widget-frame { height: 100%; } .widget-frame:fullscreen { background: rgba(10, 10, 10,
0.98); padding: 24px; box-sizing: border-box; } .widget-frame:fullscreen .widget-wrapper {
height: 100%; }
